import Badge from '@cloudscape-design/components/badge'
import Container from '@cloudscape-design/components/container'
import Header from '@cloudscape-design/components/header'
import SpaceBetween from '@cloudscape-design/components/space-between'
import Box from '@cloudscape-design/components/box'
import FormField from '@cloudscape-design/components/form-field'
import Select from '@cloudscape-design/components/select'
import Input from '@cloudscape-design/components/input'
import Textarea from '@cloudscape-design/components/textarea'
import Toggle from '@cloudscape-design/components/toggle'
import type { PresetProfile } from '@/types'
import { PROMPT_PRESETS } from '@/config/defaults'
import { getVendorFromPreset } from '@/utils/vendor-filter'
import { useAppStore } from '@/store/app-store'

interface Props {
  preset: PresetProfile
  onUpdate: (updated: PresetProfile) => void
  onToggle: (id: string) => void
}

const COMPLEXITY_OPTIONS = Object.keys(PROMPT_PRESETS) as (keyof typeof PROMPT_PRESETS)[]

function getVendorBadge(preset: PresetProfile): { label: string; color: 'blue' | 'green' | 'red' | 'grey' } {
  const vendor = getVendorFromPreset(preset)
  switch (vendor) {
    case 'bedrock': return { label: 'Bedrock', color: 'blue' }
    case 'anthropic-direct': return { label: 'Anthropic', color: 'green' }
    case 'grok': return { label: 'Grok', color: 'red' }
  }
}

export function PresetProfileCard({ preset, onUpdate, onToggle }: Props) {
  const vendorBadge = getVendorBadge(preset)
  const executionMode = useAppStore((s) => s.executionMode)
  const handleComplexityChange = (complexity: string) => {
    if (complexity === 'custom') {
      onUpdate({
        ...preset,
        complexity: 'custom',
        prompt: '',
        maxTokens: 1024
      })
    } else {
      const presetData = PROMPT_PRESETS[complexity as keyof typeof PROMPT_PRESETS]
      if (presetData) {
        onUpdate({
          ...preset,
          complexity,
          prompt: presetData.prompt,
          maxTokens: presetData.maxTokens
        })
      }
    }
  }

  const complexityOptions = [
    ...COMPLEXITY_OPTIONS.map(c => ({
      value: c,
      label: PROMPT_PRESETS[c].label
    })),
    { value: 'custom', label: 'Custom' }
  ]

  const selectedLabel = preset.complexity === 'custom'
    ? 'Custom'
    : PROMPT_PRESETS[preset.complexity as keyof typeof PROMPT_PRESETS]?.label ?? preset.complexity

  return (
    <div style={{ opacity: preset.enabled ? 1 : 0.5, transition: 'opacity 0.2s' }}>
      <Container
        header={
          <Header
            actions={
              <Toggle
                checked={preset.enabled}
                onChange={() => onToggle(preset.id)}
                ariaLabel={`Enable ${preset.name}`}
              >
                Enabled
              </Toggle>
            }
          >
            <SpaceBetween direction="horizontal" size="xs">
              <Badge color={vendorBadge.color}>{vendorBadge.label}</Badge>
            </SpaceBetween>
          </Header>
        }
      >
        <SpaceBetween size="s">
          <Box variant="h4">{preset.name}</Box>

          <FormField label="Prompt Complexity">
            <Select
              selectedOption={{ value: preset.complexity, label: selectedLabel }}
              onChange={({ detail }) => handleComplexityChange(detail.selectedOption.value ?? '')}
              options={complexityOptions}
            />
          </FormField>

          <FormField label="Prompt">
            <Textarea
              value={preset.prompt}
              onChange={({ detail }) => onUpdate({ ...preset, prompt: detail.value })}
              rows={3}
            />
          </FormField>

          <FormField label="Max Tokens">
            <Input
              type="number"
              value={String(preset.maxTokens)}
              onChange={({ detail }) =>
                onUpdate({ ...preset, maxTokens: Math.max(1, parseInt(detail.value, 10) || 1) })
              }
            />
          </FormField>

          {executionMode === 'local' && preset.profileType?.toLowerCase().includes('guardrail') && (
            <>
              <FormField
                label="Guardrail ID"
                description="The guardrail must exist in the same region and AWS account where you are testing."
              >
                <Input
                  value={preset.guardrailId ?? ''}
                  placeholder="e.g. abc123def456"
                  onChange={({ detail }) => onUpdate({ ...preset, guardrailId: detail.value })}
                />
              </FormField>
              <FormField label="Guardrail Version">
                <Input
                  value={preset.guardrailVersion ?? '1'}
                  placeholder="1 or DRAFT"
                  onChange={({ detail }) => onUpdate({ ...preset, guardrailVersion: detail.value })}
                />
              </FormField>
            </>
          )}
        </SpaceBetween>
      </Container>
    </div>
  )
}
